import { HttpHandlerFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, filter, Observable, of, shareReplay, tap } from 'rxjs';

import { CacheService, CacheTtl } from '@core/services/cache/cache';
import { LogService } from '@core/services/log/log';
import { isManagedApiUrl } from '@core/utils/api-url';
import { environment } from '@environment/environment';

export interface CacheRoute {
  pattern: string;
  ttl: CacheTtl;
  methods?: ('GET' | 'POST')[];
}

export interface InvalidationRule {
  trigger: { pattern: string; method: string };
  invalidates: string[];
}

export const CACHE_ROUTES: CacheRoute[] = [
  { pattern: '/consumer/establishment/', ttl: CacheTtl.Long },
  { pattern: '/entrees/', ttl: CacheTtl.Medium },
  { pattern: '/entree-sizes/', ttl: CacheTtl.Short },
  // Prices are a POST whose body is the deduped (establishment, entree) set; identical bodies (repeat
  // nutrient-program updates) are served from cache instead of re-hitting the network.
  { pattern: '/prices', ttl: CacheTtl.Medium, methods: ['POST'] },
  { pattern: '/settings/fulfillment-windows', ttl: CacheTtl.Long },
  { pattern: '/rating/consumables', ttl: CacheTtl.Short, methods: ['POST'] },
  { pattern: '/ratings/bulk', ttl: CacheTtl.Short, methods: ['POST'] },
  { pattern: '/rating/qualities', ttl: CacheTtl.VeryLong },
  { pattern: '/preferences/options', ttl: CacheTtl.VeryLong },
  { pattern: '/preferences/food', ttl: CacheTtl.Short, methods: ['GET'] },
  { pattern: '/preferences/standards', ttl: CacheTtl.Short, methods: ['GET'] },
  { pattern: '/preferences/dietary-restrictions', ttl: CacheTtl.Short, methods: ['GET'] }
];

export const INVALIDATION_RULES: InvalidationRule[] = [
  {
    trigger: { pattern: '/ratings', method: 'POST' },
    invalidates: ['/rating/consumables']
  },
  {
    trigger: { pattern: '/preferences/food', method: 'POST' },
    invalidates: ['/preferences/food']
  },
  {
    trigger: { pattern: '/preferences/standards', method: 'POST' },
    invalidates: ['/preferences/standards']
  },
  {
    trigger: { pattern: '/preferences/dietary-restrictions', method: 'POST' },
    invalidates: ['/preferences/dietary-restrictions']
  }
];

function matchRoute(url: string, method: string, routes: CacheRoute[]): CacheRoute | null {
  for (const route of routes) {
    const methodMatch = !route.methods || route.methods.includes(method as 'GET' | 'POST');
    if (methodMatch && url.includes(route.pattern)) {
      return route;
    }
  }
  return null;
}

function buildCacheKey(req: HttpRequest<unknown>): string {
  const base = `${req.method}:${req.urlWithParams}`;
  if (req.method === 'POST' && req.body) {
    const sortedBody = JSON.stringify(sortObject(req.body));
    return `${base}:${simpleHash(sortedBody)}`;
  }
  return base;
}

function sortObject(obj: any): any {
  if (Array.isArray(obj)) return [...obj].sort((a, b) => String(a).localeCompare(String(b)));
  if (obj && typeof obj === 'object') {
    return Object.keys(obj)
      .sort((a, b) => a.localeCompare(b))
      .reduce((sorted: any, key) => {
        sorted[key] = sortObject(obj[key]);
        return sorted;
      }, {});
  }
  return obj;
}

function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.codePointAt(i) ?? 0;
    hash = (hash << 5) - hash + char;
    hash = Math.trunc(hash);
  }
  return hash.toString(36);
}

function applyInvalidationRules(req: HttpRequest<unknown>, cacheService: CacheService, logService: LogService): void {
  for (const rule of INVALIDATION_RULES) {
    if (req.method === rule.trigger.method && req.url.includes(rule.trigger.pattern)) {
      for (const pattern of rule.invalidates) {
        logService.info('[Cache] INVALIDATE', { pattern, trigger: `${req.method} ${req.url}` });
        cacheService.invalidateByPattern(pattern);
      }
    }
  }
}

export function cacheInterceptorFn(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<any> {
  if (!isManagedApiUrl(req.url, environment.apiUrl)) {
    return next(req);
  }

  const cacheService = inject(CacheService);
  const logService = inject(LogService);

  if (req.method !== 'GET' && req.method !== 'POST') {
    applyInvalidationRules(req, cacheService, logService);
    return next(req);
  }

  const route = matchRoute(req.url, req.method, CACHE_ROUTES);

  if (!route) {
    if (req.method === 'POST') {
      applyInvalidationRules(req, cacheService, logService);
    }
    return next(req);
  }

  const key = buildCacheKey(req);

  const cached = cacheService.get(key, route.ttl);
  if (cached) {
    logService.info('[Cache] HIT', { method: req.method, url: req.urlWithParams, ttl: route.ttl });
    return of(cached.clone());
  }

  const inflight = cacheService.getInflight(key);
  if (inflight) {
    logService.info('[Cache] DEDUP', { method: req.method, url: req.urlWithParams });
    return inflight;
  }

  logService.info('[Cache] MISS', { method: req.method, url: req.urlWithParams, ttl: route.ttl });

  // Taken as the request starts: an invalidation that lands before the response keeps it out of the cache.
  const startedAt = cacheService.currentGeneration();
  const request$: Observable<HttpResponse<unknown>> = next(req).pipe(
    filter((event): event is HttpResponse<unknown> => event instanceof HttpResponse),
    tap((response) => {
      cacheService.set(key, response.clone(), startedAt);
      cacheService.clearInflight(key, request$);
    }),
    catchError((error) => {
      cacheService.clearInflight(key, request$);
      throw error;
    }),
    shareReplay(1)
  );

  cacheService.setInflight(key, request$);
  return request$;
}
